import { startTransition, useEffect, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  applyFleetOnboardingRouteContext,
  type FleetOnboardingSearch,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  OperatorEmptyState,
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import { apiClient, type FleetKitBatchRecord } from "@/lib/api/client"

interface FleetKitsPrintPageProps {
  onBackToKits: () => void
  search: FleetOnboardingSearch
}

function toDraftIssuance(batch: FleetKitBatchRecord) {
  return {
    activationMode: batch.activationMode,
    batchCode: batch.batchCode,
    batchLabel: batch.batchLabel,
    kits: batch.kits,
    preparedAt: batch.preparedAt || batch.createdAt || null,
    templateKey: batch.templateKey as
      | "intercity_bus_passenger"
      | "trucking_standard"
      | "two_wheeler_delivery_shift",
    templateLabel: batch.templateLabel,
    totalAssets: batch.totalAssets,
    totalStickers: batch.totalStickers,
    vertical: batch.vertical,
  } as const
}

export function FleetKitsPrintPage({
  onBackToKits,
  search,
}: FleetKitsPrintPageProps) {
  const { draft, setDraft } = useFleetOnboardingDraft()
  const [isHydratingRun, setIsHydratingRun] = useState(false)
  const [isHydratingBatch, setIsHydratingBatch] = useState(false)
  const [latestRunBatchCode, setLatestRunBatchCode] = useState<string | null>(
    null
  )
  const [loadError, setLoadError] = useState<string | null>(null)
  const [persistedBatch, setPersistedBatch] =
    useState<FleetKitBatchRecord | null>(null)

  useEffect(() => {
    setDraft((previous) =>
      applyFleetOnboardingRouteContext(previous, {
        mode: search.mode,
        organizationId: search.organizationId,
        runId: search.runId,
        vertical: search.vertical,
      })
    )
  }, [
    search.mode,
    search.organizationId,
    search.runId,
    search.vertical,
    setDraft,
  ])

  useEffect(() => {
    const organizationId =
      search.organizationId ??
      draft.activeOrganizationId ??
      draft.masterPersistence?.organizationId ??
      null
    if (!organizationId) {
      return
    }

    let isActive = true
    setIsHydratingRun(true)
    setLoadError(null)

    void apiClient
      .resolveFleetOnboardingRun({
        onboardingMode: search.mode ?? undefined,
        organizationId,
        runId:
          search.runId ??
          draft.activeRunId ??
          draft.masterPersistence?.runId ??
          null,
        step: "review",
        vertical: search.vertical ?? undefined,
      })
      .then((result) => {
        if (!isActive) {
          return
        }

        const resolvedLatestBatchCode =
          result.run.latestKitBatch?.batchCode || null
        setLatestRunBatchCode(resolvedLatestBatchCode)
        setDraft((previous) =>
          applyFleetOnboardingRouteContext(previous, {
            mode: result.run.onboardingMode || undefined,
            organizationId: result.run.organizationId,
            runId: result.run.id,
            vertical: result.run.vertical,
          })
        )
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setLoadError(
          error instanceof Error
            ? error.message
            : "Failed to resolve the onboarding run for print/export continuity."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsHydratingRun(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [
    draft.activeOrganizationId,
    draft.activeRunId,
    draft.masterPersistence?.organizationId,
    draft.masterPersistence?.runId,
    search.mode,
    search.organizationId,
    search.runId,
    search.vertical,
    setDraft,
  ])

  const resolvedBatchCode = search.batchCode || latestRunBatchCode
  const activeBatch = persistedBatch

  useEffect(() => {
    if (!resolvedBatchCode) {
      setPersistedBatch(null)
      return
    }

    let isActive = true
    setIsHydratingBatch(true)
    setLoadError(null)
    setPersistedBatch((previous) =>
      previous?.batchCode === resolvedBatchCode ? previous : null
    )

    void apiClient
      .getFleetKitBatch(resolvedBatchCode)
      .then((batch) => {
        if (!isActive) {
          return
        }

        setLoadError(null)
        setPersistedBatch(batch)
        startTransition(() => {
          setDraft((previous) => ({
            ...previous,
            organizationName:
              previous.organizationName ||
              batch.organizationName ||
              previous.organizationName,
            kitIssuance: toDraftIssuance(batch),
          }))
        })
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setPersistedBatch(null)
        setLoadError(
          error instanceof Error
            ? error.message
            : "Failed to load the fleet kit batch."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsHydratingBatch(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [resolvedBatchCode, setDraft])

  function exportBatchSummary() {
    if (!activeBatch) {
      return
    }

    const blob = new Blob([JSON.stringify(activeBatch, null, 2)], {
      type: "application/json",
    })
    const objectUrl = window.URL.createObjectURL(blob)
    const link = window.document.createElement("a")
    link.href = objectUrl
    link.download = `${activeBatch.batchCode}-fleet-kit-export.json`
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
    window.URL.revokeObjectURL(objectUrl)
  }

  function triggerBatchZipDownload() {
    if (!activeBatch) {
      return
    }

    if (typeof window === "undefined") {
      return
    }

    const link = window.document.createElement("a")
    link.href = apiClient.getFleetKitBatchZipDownloadPath(activeBatch.batchCode)
    link.rel = "noopener"
    window.document.body.appendChild(link)
    link.click()
    window.document.body.removeChild(link)
  }

  return (
    <FleetB2BPageShell
      activeSection="kits"
      title="Kit print and export"
      description="Prepare printable sticker sheets, dispatch/event outputs, and asset-to-QR export summaries after the issuance batch has been prepared."
      highlights={[
        "Print by issuance batch, operating unit, or selected asset set.",
        "Expose sticker slots, preview QR codes, and scan-flow ownership in one export view.",
        "Keep print output tied to kit issuance instead of the old generic stock-ops flow.",
      ]}
    >
      <div className="grid gap-5">
        {isHydratingRun ? (
          <OperatorStatusMessage>
            Resolving onboarding run continuity for persisted print/export
            reopening.
          </OperatorStatusMessage>
        ) : null}

        {isHydratingBatch ? (
          <OperatorStatusMessage>
            Loading persisted fleet kit batch{" "}
            <strong>{resolvedBatchCode}</strong>.
          </OperatorStatusMessage>
        ) : null}

        {loadError ? (
          <OperatorStatusMessage tone="danger">
            {loadError}
          </OperatorStatusMessage>
        ) : null}

        {!activeBatch ? (
          isHydratingBatch || isHydratingRun ? null : (
            <OperatorPanel
              title="No prepared batch"
              description="Print/export preparation needs a prepared issuance batch first."
            >
              <OperatorEmptyState
                title="No issuance batch is available for print."
                message="Go back to Fleet kits, prepare the system-activated issue batch, and return here for print/export preparation."
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={onBackToKits}
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Back to kits
                </button>
              </div>
            </OperatorPanel>
          )
        ) : (
          <>
            <OperatorStatusMessage>
              This print/export view is now backed by the persisted fleet kit
              batch. Reloading by batch code returns the same issue set instead
              of relying on session-only state.
            </OperatorStatusMessage>

            <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
              <OperatorPanel
                title="Batch export summary"
                description="Use this as the print and dispatch control surface for the prepared issue set."
              >
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <OperatorMetricCard
                    label="Batch code"
                    value={activeBatch.batchCode}
                    detail="Reference for print signoff and dispatch tracking."
                  />
                  <OperatorMetricCard
                    label="Assets"
                    value={activeBatch.totalAssets}
                    detail="Assets currently included in the prepared issue set."
                  />
                  <OperatorMetricCard
                    label="Stickers"
                    value={activeBatch.totalStickers}
                    detail="Total sticker count across all kits in the batch."
                  />
                  <OperatorMetricCard
                    label="Activation"
                    value={activeBatch.activationMode}
                    detail="B2B kits are system-activated rather than public self-onboarded."
                  />
                  <OperatorMetricCard
                    label="Organization"
                    value={
                      activeBatch.organizationName ||
                      draft.organizationName ||
                      "Not captured"
                    }
                    detail="Organization context attached to this issuance batch."
                  />
                  <OperatorMetricCard
                    label="Prepared at"
                    value={
                      activeBatch.preparedAt
                        ? new Date(activeBatch.preparedAt).toLocaleString()
                        : "Not recorded"
                    }
                    detail="Persistent batch timestamp used for reload-safe continuity."
                  />
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Print checklist"
                description="Operational guardrails before issuing physical stickers to the field."
              >
                <div className="grid gap-2 text-sm text-muted-foreground">
                  <div className="rounded-2xl border border-border/60 px-3 py-2">
                    Confirm every asset number maps to the correct branch,
                    depot, hub, or route base.
                  </div>
                  <div className="rounded-2xl border border-border/60 px-3 py-2">
                    Validate public versus private sticker placement before
                    field dispatch.
                  </div>
                  <div className="rounded-2xl border border-border/60 px-3 py-2">
                    Export and archive the batch code with the issuance date and
                    operator signoff.
                  </div>
                </div>
              </OperatorPanel>
            </section>

            <OperatorPanel
              title="Prepared print layout"
              description="Each asset shows the stickers that should be printed together as one issuance kit."
            >
              <div className="grid gap-4">
                {activeBatch.kits.map((kit) => (
                  <div
                    key={`${kit.assetNumber}-${kit.operatingUnitLabel}`}
                    className="rounded-3xl border border-border/70 bg-background/70 px-4 py-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-lg font-semibold">
                          {kit.assetNumber}
                        </div>
                        <div className="mt-1 text-sm text-muted-foreground">
                          {kit.operatingUnitLabel} • {kit.operatorLabel}
                        </div>
                      </div>
                      <div className="rounded-full border border-border/70 px-3 py-1 text-xs font-medium tracking-[0.16em] text-muted-foreground uppercase">
                        {kit.assetTypeLabel}
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                      {kit.slots.map((slot) => (
                        <div
                          key={`${kit.assetNumber}-${slot.slotKey}`}
                          className="rounded-2xl border border-dashed border-border/70 bg-background px-3 py-3"
                        >
                          <div className="text-sm font-semibold">
                            {slot.label}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {slot.visibility} • {slot.scanFlow}
                          </div>
                          <div className="mt-3 rounded-xl border border-border/60 bg-muted/40 px-2 py-2 font-mono text-xs">
                            {slot.previewCode}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                >
                  Print batch
                </button>
                <button
                  type="button"
                  onClick={triggerBatchZipDownload}
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Download ZIP
                </button>
                <button
                  type="button"
                  onClick={exportBatchSummary}
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Export batch JSON
                </button>
                <button
                  type="button"
                  onClick={onBackToKits}
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Back to kits
                </button>
              </div>
            </OperatorPanel>
          </>
        )}
      </div>
    </FleetB2BPageShell>
  )
}
